import { useParams, Link, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { blogArticles } from "@/mocks/blog";

export default function BlogArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const article = blogArticles.find((a) => a.slug === slug);
  const related = blogArticles.filter((a) => a.slug !== slug).slice(0, 3);

  useEffect(() => {
    if (!article) {
      navigate("/blog");
    }
    window.scrollTo(0, 0);
  }, [article, navigate]);

  if (!article) return null;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero image */}
      <div className="relative w-full h-64 md:h-96 mt-20">
        <img
          src={article.image}
          alt={article.title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 max-w-4xl mx-auto px-4 md:px-8 pb-8">
          <div className="flex items-center gap-2 mb-3">
            <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${article.categoryColor}`}>{article.category}</span>
            <span className="text-gray-300 text-xs">{article.date}</span>
            <span className="text-gray-300 text-xs flex items-center gap-1">
              <i className="ri-time-line"></i> {article.readTime} de lecture
            </span>
          </div>
          <h1 className="text-2xl md:text-4xl font-extrabold text-white leading-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            {article.title}
          </h1>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 md:px-8 py-10 md:py-16">

        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-gray-400 mb-8">
          <Link to="/" className="hover:text-[#00a340] transition-colors cursor-pointer">Accueil</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/blog" className="hover:text-[#00a340] transition-colors cursor-pointer">Blog</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-gray-600 truncate max-w-xs">{article.title}</span>
        </nav>

        <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
          {/* Article content */}
          <article className="flex-1 min-w-0">
            {/* Author */}
            <div className="flex items-center gap-3 mb-8 p-4 rounded-xl bg-[#f5fdf8] border border-[#e6f7ed]">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#00a340] text-white font-black text-sm flex-shrink-0">EV</div>
              <div>
                <div className="font-bold text-gray-800 text-sm">{article.author}</div>
                <div className="text-xs text-gray-500">{article.authorRole}</div>
              </div>
            </div>

            {/* Excerpt */}
            <p className="text-gray-600 text-base md:text-lg leading-relaxed mb-8 font-medium border-l-4 border-[#00a340] pl-5 italic">
              {article.excerpt}
            </p>

            {/* Content sections */}
            <div className="flex flex-col gap-5">
              {article.content.map((section, i) => {
                if (section.type === "paragraph") {
                  return (
                    <p key={i} className="text-gray-700 text-sm md:text-base leading-relaxed text-justify">
                      {section.text}
                    </p>
                  );
                }
                if (section.type === "heading") {
                  return (
                    <h2 key={i} className="text-lg md:text-2xl font-extrabold text-gray-900 mt-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                      {section.text}
                    </h2>
                  );
                }
                if (section.type === "list") {
                  return (
                    <ul key={i} className="flex flex-col gap-2.5">
                      {section.items?.map((item, j) => (
                        <li key={j} className="flex items-start gap-3 text-sm md:text-base text-gray-700 leading-relaxed">
                          <span className="w-5 h-5 flex items-center justify-center rounded-full bg-[#e6f7ed] text-[#00a340] flex-shrink-0 mt-0.5">
                            <i className="ri-check-line text-xs"></i>
                          </span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  );
                }
                if (section.type === "tip") {
                  return (
                    <div key={i} className="flex gap-3 p-4 rounded-xl bg-[#e6f7ed] border border-[#a8f0c0]">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#00a340] text-white flex-shrink-0">
                        <i className="ri-lightbulb-line text-sm"></i>
                      </div>
                      <p className="text-gray-700 text-sm leading-relaxed">{section.text}</p>
                    </div>
                  );
                }
                if (section.type === "quote") {
                  return (
                    <blockquote key={i} className="p-5 rounded-xl bg-[#0d2b18] text-green-100 text-sm leading-relaxed italic border-l-4 border-[#00a340]">
                      {section.text}
                    </blockquote>
                  );
                }
                return null;
              })}
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mt-10 pt-8 border-t border-gray-100">
              {article.tags.map((tag) => (
                <span key={tag} className="px-3 py-1.5 rounded-full bg-[#f5fdf8] text-[#00a340] text-xs font-semibold border border-[#e6f7ed]">
                  #{tag}
                </span>
              ))}
            </div>

            {/* CTA */}
            <div className="mt-10 p-6 md:p-8 rounded-2xl bg-[#0d2b18] flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <div className="text-white font-extrabold text-base mb-1" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  Besoin d&apos;un devis étanchéité ?
                </div>
                <div className="text-gray-400 text-xs">Réponse sous 48h, gratuit et sans engagement.</div>
              </div>
              <Link
                to="/devis"
                className="flex-shrink-0 px-6 py-3 rounded-md bg-[#00a340] text-white font-bold text-sm whitespace-nowrap hover:bg-[#007a30] transition-colors cursor-pointer"
              >
                Demander un devis
              </Link>
            </div>
          </article>

          {/* Sidebar */}
          <aside className="w-full lg:w-72 flex-shrink-0">
            {/* Contact rapide */}
            <div className="rounded-2xl bg-[#f5fdf8] border border-[#e6f7ed] p-5 mb-6">
              <h3 className="font-extrabold text-gray-900 text-sm mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                Contactez nos experts
              </h3>
              <div className="flex flex-col gap-3">
                <a href="tel:0175434346" className="flex items-center gap-3 p-3 rounded-xl bg-white border border-gray-100 hover:border-[#a8f0c0] transition-colors cursor-pointer group">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#e6f7ed] text-[#00a340] flex-shrink-0 group-hover:bg-[#00a340] group-hover:text-white transition-colors">
                    <i className="ri-phone-line text-sm"></i>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">Appelez-nous</div>
                    <div className="text-sm font-bold text-gray-800">01 75 43 43 46</div>
                  </div>
                </a>
                <a href="mailto:contact@ksbtp.fr" className="flex items-center gap-3 p-3 rounded-xl bg-white border border-gray-100 hover:border-[#a8f0c0] transition-colors cursor-pointer group">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#e6f7ed] text-[#00a340] flex-shrink-0 group-hover:bg-[#00a340] group-hover:text-white transition-colors">
                    <i className="ri-mail-line text-sm"></i>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">Écrivez-nous</div>
                    <div className="text-sm font-bold text-gray-800">contact@ksbtp.fr</div>
                  </div>
                </a>
              </div>
              <Link
                to="/devis"
                className="mt-4 w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-[#00a340] text-white text-sm font-bold whitespace-nowrap hover:bg-[#007a30] transition-colors cursor-pointer"
              >
                Devis gratuit <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            {/* Articles liés */}
            <div>
              <h3 className="font-extrabold text-gray-900 text-sm mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                Articles similaires
              </h3>
              <div className="flex flex-col gap-4">
                {related.map((rel) => (
                  <Link
                    key={rel.slug}
                    to={`/blog/${rel.slug}`}
                    className="flex gap-3 group cursor-pointer"
                  >
                    <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                      <img src={rel.image} alt={rel.title} className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-110" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold mb-1 ${rel.categoryColor}`}>{rel.category}</span>
                      <p className="text-xs font-bold text-gray-800 leading-snug group-hover:text-[#00a340] transition-colors line-clamp-2">{rel.title}</p>
                      <span className="text-[10px] text-gray-400">{rel.date}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>

        {/* Back to blog */}
        <div className="mt-12 pt-8 border-t border-gray-100">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-[#00a340] font-bold text-sm hover:gap-3 transition-all cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Retour au blog
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  );
}
